<script>
  import { createEventDispatcher } from 'svelte';

  export let apps = [];
  const dispatch = createEventDispatcher();

  let dragIndex = null;
  let dragOverIndex = null;

  function handleDragStart(e, index) {
    dragIndex = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
    e.target.classList.add('dragging');
  }

  function handleDragEnd(e) {
    e.target.classList.remove('dragging');
    dragIndex = null;
    dragOverIndex = null;
  }

  function handleDragOver(e, index) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dragOverIndex = index;
  }

  function handleDragLeave() {
    dragOverIndex = null;
  }

  function handleDrop(e, targetIndex) {
    e.preventDefault();
    const sourceIndex = dragIndex;

    if (sourceIndex === null || sourceIndex === targetIndex) return;

    // Reorder
    const newApps = [...apps];
    const [moved] = newApps.splice(sourceIndex, 1);
    newApps.splice(targetIndex, 0, moved);

    // Update order values
    const arrangement = newApps.map((app, i) => ({
      id: app.id,
      order: i + 1,
      visible: app.visible !== false,
    }));

    dispatch('reorder', { arrangement, apps: newApps });
    dragIndex = null;
    dragOverIndex = null;
  }

  function toggleVisibility(index) {
    const newApps = [...apps];
    newApps[index] = { ...newApps[index], visible: !newApps[index].visible };

    const arrangement = newApps.map((app, i) => ({
      id: app.id,
      order: i + 1,
      visible: app.visible !== false,
    }));

    dispatch('reorder', { arrangement, apps: newApps });
  }
</script>

<div class="arrangement-card card">
  <div class="card-title">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
    App Arrangement
  </div>
  <p class="arrange-hint">Drag to reorder • Toggle visibility</p>

  <div class="sort-list" id="app-arrangement-list">
    {#each apps as app, i}
      <div
        class="sort-item"
        class:drag-over={dragOverIndex === i}
        class:hidden-app={app.visible === false}
        draggable="true"
        on:dragstart={(e) => handleDragStart(e, i)}
        on:dragend={handleDragEnd}
        on:dragover={(e) => handleDragOver(e, i)}
        on:dragleave={handleDragLeave}
        on:drop={(e) => handleDrop(e, i)}
        role="listitem"
      >
        <div class="sort-handle">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/>
          </svg>
        </div>
        <div class="sort-color" style="background: {app.color}"></div>
        <span class="sort-name">{app.name}</span>
        <button
          class="visibility-btn"
          class:hidden={app.visible === false}
          on:click={() => toggleVisibility(i)}
          title={app.visible === false ? 'Show' : 'Hide'}
        >
          {#if app.visible !== false}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
            <span>Show</span>
          {:else}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
            <span>Hide</span>
          {/if}
        </button>
      </div>
    {/each}
  </div>
</div>

<style>
  .arrangement-card {
    animation: fadeInUp 0.4s ease both;
  }

  .arrangement-card .card-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .arrangement-card .card-title svg {
    color: var(--neon-blue);
  }

  .arrange-hint {
    font-size: 12px;
    color: var(--text-tertiary);
    margin-bottom: 16px;
  }

  .sort-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .sort-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    cursor: grab;
    transition: all 0.2s ease;
    user-select: none;
  }

  .sort-item:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: var(--border-default);
  }

  .sort-item.drag-over {
    border-color: var(--neon-blue);
    background: rgba(59, 130, 246, 0.06);
    box-shadow: 0 0 12px rgba(59, 130, 246, 0.1);
  }

  .sort-item.hidden-app {
    opacity: 0.5;
  }

  :global(.sort-item.dragging) {
    opacity: 0.4;
    transform: scale(0.98);
  }

  .sort-handle {
    color: var(--text-tertiary);
    cursor: grab;
    flex-shrink: 0;
  }

  .sort-handle:active { cursor: grabbing; }

  .sort-color {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    box-shadow: 0 0 6px currentColor;
  }

  .sort-name {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .visibility-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: var(--radius-sm);
    font-size: 12px;
    font-weight: 500;
    color: var(--neon-green);
    background: rgba(16, 185, 129, 0.08);
    border: 1px solid rgba(16, 185, 129, 0.2);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .visibility-btn:hover {
    background: rgba(16, 185, 129, 0.15);
  }

  .visibility-btn.hidden {
    color: var(--text-tertiary);
    background: rgba(255, 255, 255, 0.03);
    border-color: var(--border-default);
  }

  .visibility-btn.hidden:hover {
    color: var(--text-secondary);
    background: rgba(255, 255, 255, 0.05);
  }
</style>
